import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScrollIndicatorProps {
  containerRef: React.RefObject<HTMLDivElement>;
  className?: string;
}

/**
 * ScrollIndicator component that shows visual hints when content is scrollable
 * Displays fade gradients and arrow indicators on the edges
 */
export function ScrollIndicator({ containerRef, className }: ScrollIndicatorProps) {
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftIndicator(scrollLeft > 10);
      setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 10);
    };

    // Check initially and on scroll
    checkScroll();
    container.addEventListener('scroll', checkScroll);
    
    // Also check on resize
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <>
      {/* Left scroll indicator */}
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-0 bottom-0 w-12 z-10 transition-opacity duration-300',
          'bg-gradient-to-r from-background to-transparent',
          showLeftIndicator ? 'opacity-100' : 'opacity-0',
          className
        )}
      >
        <div className="flex items-center justify-center h-full">
          <ChevronLeft className="h-5 w-5 text-muted-foreground animate-pulse" />
        </div>
      </div>

      {/* Right scroll indicator */}
      <div
        className={cn(
          'pointer-events-none absolute right-0 top-0 bottom-0 w-12 z-10 transition-opacity duration-300',
          'bg-gradient-to-l from-background to-transparent',
          showRightIndicator ? 'opacity-100' : 'opacity-0',
          className
        )}
      >
        <div className="flex items-center justify-center h-full">
          <ChevronRight className="h-5 w-5 text-muted-foreground animate-pulse" />
        </div>
      </div>
    </>
  );
}
